import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Eyebrow, MaskLines, Reveal } from "./Motion";
import { SERVICES } from "../data/content";
import { WhatsAppButton } from "./WhatsApp";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : null;
const empty = { full_name: "", email: "", phone: "", service: SERVICES[0].title, message: "" };

const Field = ({ label, children }) => (
  <label className="block">
    <span className="mb-2 block text-sm tracking-[0.12em] uppercase text-[#CFC8BD]">{label}</span>
    {children}
  </label>
);

export const Contact = () => {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    const onSel = (e) => { setForm((f) => ({ ...f, service: e.detail })); };
    window.addEventListener("select-service", onSel);
    return () => window.removeEventListener("select-service", onSel);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!API) { toast.error("Booking is temporarily unavailable."); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      toast.success("Thank you! Ulyana will get back to you within 1–2 days.");
      setForm(empty);
    } catch (err) {
      toast.error("Please check your name and email and try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="bg-[#161513] text-[#FAF8F5]">
      <div className="mx-auto grid max-w-[1440px] gap-14 px-5 py-24 sm:px-10 sm:py-32 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <Eyebrow light>Book a consultation</Eyebrow>
          <h2 className="mt-6 font-display text-4xl leading-[1.04] sm:text-5xl lg:text-6xl">
            <MaskLines lines={["Let's start", <span key="i" className="italic text-[#D8C3A0]">with a conversation.</span>]} />
          </h2>
          <Reveal delay={0.1}>
            <p className="mt-8 text-lg leading-relaxed text-[#CFC8BD]">
              Tell me a little about yourself and which service interests you. I'll reply personally with available times and pricing for your request.
            </p>
            <p className="mt-6 text-sm tracking-[0.2em] uppercase text-[#D8C3A0]">In person in New York · Online worldwide</p>
            <div className="mt-10 border-t border-white/10 pt-8">
              <p className="text-sm tracking-[0.12em] uppercase text-[#CFC8BD]">Prefer to chat?</p>
              <WhatsAppButton testId="contact-whatsapp-button" className="mt-4 rounded-full border border-white/20 px-6 py-3 text-base text-[#FAF8F5] transition-colors duration-300 hover:border-[#25D366] hover:bg-[#25D366] hover:text-white">
                Message me on WhatsApp
              </WhatsAppButton>
            </div>
          </Reveal>
        </div>
        <Reveal className="lg:col-span-6 lg:col-start-7" delay={0.15}>
          <form onSubmit={submit} className="space-y-7" data-testid="contact-form">
            <div className="grid gap-7 sm:grid-cols-2">
              <Field label="Your name *"><input required value={form.full_name} onChange={set("full_name")} className="field" data-testid="input-full-name" /></Field>
              <Field label="Email *"><input required type="email" value={form.email} onChange={set("email")} className="field" data-testid="input-email" /></Field>
            </div>
            <div className="grid gap-7 sm:grid-cols-2">
              <Field label="Phone"><input type="tel" value={form.phone} onChange={set("phone")} className="field" data-testid="input-phone" /></Field>
              <Field label="Service *">
                <select value={form.service} onChange={set("service")} className="field cursor-pointer" data-testid="select-service">
                  {SERVICES.map((s) => <option key={s.id} value={s.title} className="text-black">{s.title}</option>)}
                  <option value="Not sure yet" className="text-black">Not sure yet</option>
                </select>
              </Field>
            </div>
            <Field label="Tell me about your goals"><textarea rows={4} value={form.message} onChange={set("message")} className="field resize-none" data-testid="input-message" /></Field>
            <button type="submit" disabled={loading} className="btn-light w-full justify-center sm:w-auto" data-testid="btn-submit-inquiry">
              {loading && <Loader2 className="h-4 w-4 animate-spin" />} Send request
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
